/**
 * AI Citation Analysis API Client
 * Integrates with dash-api for statement extraction, citation analysis, and suggestions
 */

import {API_BASE} from './reports';
import {getApiBase} from '../../config/apiBase';
import buildHeaders from './fetchHeaders';
import {toBackendIcr} from '../formatIcrDescription';

// Timeout for AI analysis endpoints (5 minutes)
const AI_ANALYSIS_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 2000; // 2 seconds
const RETRY_STATUS_CODES = [502, 504]; // Bad Gateway, Gateway Timeout

/**
 * Sleep for a specified duration
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetch with timeout support
 * @param {string} url - URL to fetch
 * @param {object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Response>}
 */
const fetchWithTimeout = async (url, options = {}, timeout = AI_ANALYSIS_TIMEOUT) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
        const response = await fetch(url, {
            ...options,
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error(`Request timeout after ${timeout / 1000} seconds`);
        }
        throw error;
    }
};

/**
 * Fetch with timeout and automatic retry on transient errors (502, 504)
 * Uses exponential backoff: 2s, 4s, 8s
 * @param {string} url - URL to fetch
 * @param {object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @param {number} maxRetries - Maximum number of retries
 * @returns {Promise<Response>}
 */
const fetchWithRetry = async (url, options = {}, timeout = AI_ANALYSIS_TIMEOUT, maxRetries = MAX_RETRIES) => {
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            console.log(`🔄 API request attempt ${attempt + 1}/${maxRetries + 1}: ${url}`);

            const response = await fetchWithTimeout(url, options, timeout);

            // Check if we got a retryable error status
            if (RETRY_STATUS_CODES.includes(response.status)) {
                const errorMsg = `${response.status} ${response.statusText}`;
                console.warn(`⚠️ Attempt ${attempt + 1} failed with ${errorMsg}`);

                // If we have retries left, wait and try again
                if (attempt < maxRetries) {
                    const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt); // Exponential backoff
                    console.log(`⏳ Retrying in ${delay / 1000}s...`);
                    await sleep(delay);
                    continue;
                }

                // No more retries, throw error
                throw new Error(`Request failed after ${maxRetries + 1} attempts: ${errorMsg}`);
            }

            // Success or non-retryable error
            if (attempt > 0) {
                console.log(`✅ Request succeeded on attempt ${attempt + 1}`);
            }
            return response;

        } catch (error) {
            lastError = error;
            console.error(`❌ Attempt ${attempt + 1} failed:`, error.message);

            // If it's a timeout or network error and we have retries left, try again
            if (attempt < maxRetries) {
                const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt); // Exponential backoff
                console.log(`⏳ Retrying in ${delay / 1000}s...`);
                await sleep(delay);
                continue;
            }

            // No more retries
            console.error(`❌ Request failed after ${maxRetries + 1} attempts`);
            throw lastError;
        }
    }

    // Should never reach here, but just in case
    throw lastError || new Error('Request failed');
};

/**
 * Phase 1: Extract statements from report content
 * Uses automatic retry on 502/504 errors with exponential backoff
 * @param {string} reportId - Report ID
 * @param {object} content - TipTap JSON content
 * @param {Array} userFragments - User-created fragments
 * @param {boolean} forceRefresh - Force new analysis (bypass cache)
 * @returns {Promise<Object>} Analysis response with statements
 */
export const analyzeStatements = async (reportId, content, userFragments = [], forceRefresh = false) => {
    try {
        console.log('📤 Analyzing statements (Phase 1)...');
        const response = await fetchWithRetry(`${API_BASE}/products/${reportId}/analyze/statements`, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                reportId,
                content,
                userFragments: userFragments.map(frag => ({
                    uid: frag.uid,
                    text: frag.text,
                    startOffset: frag.startOffset,
                    endOffset: frag.endOffset,
                    start: frag.start,
                    end: frag.end
                })),
                forceRefresh
            })
        });

        if (!response.ok) {
            throw new Error(`Statement analysis failed: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('✅ Statement analysis complete');
        return result;
    } catch (error) {
        console.error('❌ Error analyzing statements:', error);
        throw error;
    }
};

/**
 * Phase 2: Analyze citation quality for statements
 * Uses automatic retry on 502/504 errors with exponential backoff
 * @param {string} reportId - Report ID
 * @param {Array} statements - Statements from Phase 1
 * @param {Object} fragmentCitations - Map of fragmentUid -> citations
 * @param {string} fullReportText - Full plain text of report (for CONCLUSION analysis)
 * @returns {Promise<Object>} Citation analysis response
 */
export const analyzeCitations = async (reportId, statements, fragmentCitations, fullReportText) => {
    try {
        console.log('📤 Analyzing citations (Phase 2)...');
        const response = await fetchWithRetry(`${API_BASE}/products/${reportId}/analyze/citations`, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                reportId,
                statements: statements.map(stmt => ({
                    id: stmt.id,
                    text: stmt.text,
                    fullContext: stmt.fullContext,
                    category: stmt.category,
                    linkedFragmentUid: stmt.linkedFragmentUid,
                    requiresProof: stmt.requiresProof
                })),
                fragmentCitations,
                fullReportText
            })
        });

        if (!response.ok) {
            throw new Error(`Citation analysis failed: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('✅ Citation analysis complete');
        return result;
    } catch (error) {
        console.error('❌ Error analyzing citations:', error);
        throw error;
    }
};

/**
 * Analyze how well a report covers an ICR and surface new, well-supported topics.
 * @param {string} reportId - Report ID
 * @param {object} icr - Structured ICR details
 * @param {object} content - TipTap JSON content of the report
 * @returns {Promise<Object>} Coverage response
 */
export const analyzeReportCoverage = async (reportId, icr, content) => {
    try {
        const backendIcr = toBackendIcr(icr);
        const response = await fetchWithRetry(`${API_BASE}/products/${reportId}/report/coverage-summary`, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                reportId,
                icr: backendIcr,
                reportContent: content,
            })
        });

        if (!response.ok) {
            throw new Error(`Report coverage analysis failed: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('❌ Error analyzing report coverage:', error);
        throw error;
    }
};

/**
 * Requirement-driven draft ICR coverage (v2) using live editor claims.
 */
export const analyzeReportCoverageV2 = async (reportId, payload) => {
    try {
        const response = await fetchWithRetry(`${API_BASE}/products/${reportId}/report/icr-coverage`, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error(`Draft ICR coverage failed: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('❌ Error analyzing draft ICR coverage:', error);
        throw error;
    }
};

/**
 * Phase 3: Suggest citations from source package for a statement
 * @param {string} reportId - Report ID
 * @param {string} statementId - Statement ID
 * @param {string} statementText - Statement text needing proof
 * @param {number} maxSuggestions - Maximum suggestions to return (1-10)
 * @returns {Promise<Object>} Suggestion response
 */
export const suggestCitations = async (reportId, statementId, statementText, maxSuggestions = 5) => {
    try {
        const response = await fetchWithTimeout(`${API_BASE}/products/${reportId}/analyze/suggestions`, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                reportId,
                statementId,
                statementText,
                maxSuggestions
            })
        });

        if (!response.ok) {
            throw new Error(`Citation suggestion failed: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error suggesting citations:', error);
        throw error;
    }
};

/**
 * MINSKY-17131: find a (U)-classified (or otherwise less-restrictive) alternative for a single
 * citation whose source document is classified above the desired level. Backed by the already
 * -existing `AlternativeCitationService` (searches the corpus for a segment at/under
 * `desiredClassificationLevel` covering the same statement, scored + reasoned by an LLM).
 * @param {string} reportId - Report ID for context
 * @param {string} segmentText - The statement text the citation supports
 * @param {string} existingReferenceId - Composite reference id: docId:startSeg:startIdx:endSeg:endIdx
 *   for corpus citations; opaque namespaced id (e.g. `d:...`, `r:...`, `h:...`) for My Data /
 *   Report / Highlight citations — pass `citationText`/`originalClassificationLevel` below for those.
 * @param {'U'|'S'} [desiredClassificationLevel] - Must be strictly less restrictive than the
 *   citation's own classification level; backend only accepts 'U' or 'S'. Defaults to 'U'.
 * @param {object} [context]
 * @param {string} [context.citationText] - The citation's own text, already known by the caller.
 *   Required (together with originalClassificationLevel) for non-corpus citations, whose
 *   existingReferenceId the backend cannot parse into docId/segment coordinates.
 * @param {string} [context.originalClassificationLevel] - Classification level of the citation's
 *   source document, already known by the caller. See citationText.
 * @returns {Promise<Object>} AlternativeCitationResponse: {score, description, queries,
 *   suggestedCitations[], originalClassificationLevel, targetClassificationLevel}
 */
export const findAlternativeCitation = async (
    reportId,
    segmentText,
    existingReferenceId,
    desiredClassificationLevel = 'U',
    context = {},
) => {
    try {
        const {citationText, originalClassificationLevel} = context;
        const response = await fetchWithTimeout(`${API_BASE}/analysis/alternative-citation`, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                reportId,
                segmentText,
                existingReferenceId,
                desiredClassificationLevel,
                citationText,
                originalClassificationLevel,
            })
        });

        if (!response.ok) {
            throw new Error(`Alternative citation search failed: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('❌ Error finding alternative citation:', error);
        throw error;
    }
};

/**
 * Re-analyze a single statement after user adds a citation
 * @param {string} reportId - Report ID
 * @param {object} statement - Statement object with id, text, category, etc.
 * @param {object} fragmentCitations - Map of fragment UID to citations array
 * @param {string} fullReportText - Full report text for context
 * @returns {Promise<object>} Updated citation analysis for the statement
 */
export const reanalyzeStatement = async (reportId, statement, fragmentCitations, fullReportText) => {
    try {
        const response = await fetchWithTimeout(`${API_BASE}/products/${reportId}/analyze/statement`, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                reportId,
                statements: [statement], // Single statement in array
                fragmentCitations,
                fullReportText
            })
        });

        if (!response.ok) {
            throw new Error(`Statement re-analysis failed: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error re-analyzing statement:', error);
        throw error;
    }
};

/**
 * Phase 4: Generate alternative hypotheses for a report
 * Uses automatic retry on 502/504 errors with exponential backoff
 * @param {string} reportId - Report ID
 * @param {object} content - TipTap JSON content
 * @param {string} userInstructions - Optional user instructions for generation focus
 * @returns {Promise<Object>} Response with alternatives
 */
export const generateAlternativeConclusions = async (reportId, content, userInstructions = null) => {
    try {
        console.log('📤 Generating alternative hypotheses...');
        const response = await fetchWithRetry(`${API_BASE}/analysis/alternative-hypotheses/generate`, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                reportId,
                content,
                userInstructions
            })
        });

        if (!response.ok) {
            throw new Error(`Alternative hypotheses generation failed: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('✅ Alternative hypotheses generated:', result.alternatives?.length || 0);
        return result;
    } catch (error) {
        console.error('❌ Error generating alternative hypotheses:', error);
        throw error;
    }
};

/**
 * MINSKY-16818 (ticket 02): start a durable forecast-generation job for a report, optionally
 * including Persona opinions. Runs on the server in the background — this call only creates the
 * job and returns immediately (job status `queued`); progress/result are fetched via
 * {@link getLatestForecastJob} or the live phase-event stream ({@code forecastJobStreamUrl}).
 * Uses automatic retry on 502/504 errors with exponential backoff.
 * @param {string} reportId - Report ID
 * @param {object} content - TipTap JSON content
 * @param {object} [options]
 * @param {string} [options.userInstructions] - Optional user instructions for generation focus
 * @param {'1_MONTH'|'3_MONTHS'|'6_MONTHS'|'1_YEAR'} [options.horizon] - Forecast horizon
 * @param {0|1|2|3} [options.creativity] - Factual (0) to creative (3) dial
 * @param {string[]} [options.personaHandles] - Handles of selected personas to include opinions for
 * @param {string} [options.existingForecastText] - Plain text of a forecast already added to the
 *   document (under the "FORECAST" heading), if any — passed so the model can avoid repeating
 *   those predictions instead of the section being stripped from the main report content.
 * @param {Array<{handle: string, displayName?: string, suitabilityScore?: number, reason?: string}>} [options.personaSuggestions] -
 *   Restore-on-reload metadata only: whatever "Suggest personas" suitability suggestions were
 *   showing when the user clicked Generate. Never used for generation itself — persisted in the
 *   job's requestContext purely so a page refresh can re-show the same suitability chips/reasons.
 * @param {number|null} [options.reportVersionNumber] - The report_versions.version integer that
 *   was current in the editor at the moment the user clicked Generate, so the history view can
 *   show which document snapshot produced each past forecast. May be null when the report has
 *   never been saved as a version snapshot yet.
 * @returns {Promise<Object>} The newly created job: {id, reportId, status, currentPhase}
 */
export const startForecastJob = async (reportId, content, options = {}) => {
    const {
        userInstructions = null,
        horizon = '3_MONTHS',
        creativity = 1,
        personaHandles = [],
        existingForecastText = null,
        personaSuggestions = null,
        reportVersionNumber = null,
    } = options;

    try {
        console.log('📤 Starting forecast job...', {horizon, creativity, personaHandles});
        const response = await fetchWithRetry(`${API_BASE}/analysis/forecast/generate`, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                reportId,
                content,
                userInstructions,
                horizon,
                creativity,
                personaHandles,
                existingForecastText,
                personaSuggestions,
                reportVersionNumber,
            })
        });

        if (response.status === 409) {
            throw new Error('A forecast is already being generated for this report.');
        }
        if (!response.ok) {
            throw new Error(`Forecast job start failed: ${response.status} ${response.statusText}`);
        }

        const job = await response.json();
        console.log('✅ Forecast job started:', job.id, job.status);
        return job;
    } catch (error) {
        console.error('❌ Error starting forecast job:', error);
        throw error;
    }
};

/**
 * MINSKY-16818 (ticket 02): the latest forecast job for a report (status, current phase, and the
 * full result if completed) — powers reload/navigate-back restore without needing to already
 * hold a job id in memory. Returns {@code null} if no job has ever been started for this report.
 * @param {string} reportId - Report ID
 * @returns {Promise<Object|null>}
 */
export const getLatestForecastJob = async (reportId) => {
    const response = await fetchWithTimeout(
        `${API_BASE}/analysis/forecast/jobs/latest?reportId=${encodeURIComponent(reportId)}`,
        {headers: buildHeaders({'Accept': 'application/json'})},
        30 * 1000,
    );

    if (response.status === 404) {
        return null;
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch latest forecast job: ${response.status} ${response.statusText}`);
    }
    return await response.json();
};

/**
 * MINSKY-16818 (ticket 03): cancel an in-progress forecast job. A no-op server-side if the job is
 * already completed/failed/cancelled.
 * @param {string} jobId
 */
export const cancelForecastJob = async (jobId) => {
    const response = await fetchWithTimeout(
        `${API_BASE}/analysis/forecast/jobs/${encodeURIComponent(jobId)}/cancel`,
        {method: 'POST', headers: buildHeaders({})},
        30 * 1000,
    );

    if (!response.ok) {
        throw new Error(`Failed to cancel forecast job: ${response.status} ${response.statusText}`);
    }
};

/**
 * Forecast History View: delete a single past forecast job. Only the report owner may delete
 * (enforced server-side); the backend resolves ownership from the parent report rather than from
 * anything passed in this call.
 * @param {string} jobId
 */
export const deleteForecastJob = async (jobId) => {
    const response = await fetchWithTimeout(
        `${API_BASE}/analysis/forecast/jobs/${encodeURIComponent(jobId)}`,
        {method: 'DELETE', headers: buildHeaders({})},
        30 * 1000,
    );

    if (!response.ok) {
        throw new Error(`Failed to delete forecast job: ${response.status} ${response.statusText}`);
    }
};

/** SSE endpoint for a forecast job's live phase-event stream (ticket 02). */
export const forecastJobStreamUrl = (jobId) =>
    `${API_BASE}/analysis/forecast/jobs/${encodeURIComponent(jobId)}/stream`;

/**
 * AI-suggested Forecast personas: ranks every real Expert/Analyst persona candidate by
 * suitability for this report/question, with a short reason per persona — powers the persona
 * picker's "Suggest personas" action. Never throws; degrades to an empty array on any failure so
 * manual persona selection always remains usable even if suggestion fails.
 * @param {string} reportId - Report ID
 * @param {object} content - TipTap JSON content
 * @param {string} [userInstructions] - Optional free-form instructions steering the suggestion
 * @returns {Promise<Array<{handle: string, displayName: string, suitabilityScore: number, reason: string}>>}
 */
export const suggestPersonas = async (reportId, content, userInstructions = null) => {
    try {
        const response = await fetchWithTimeout(`${API_BASE}/analysis/forecast/personas/suggest`, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({reportId, content, userInstructions}),
        }, 60 * 1000);

        if (!response.ok) {
            throw new Error(`Failed to suggest personas: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        return Array.isArray(data?.suggestions) ? data.suggestions : [];
    } catch (error) {
        console.error('❌ Error suggesting forecast personas:', error);
        throw error;
    }
};

// Same auth header pattern as useReportTasks.js — plain fetch + Bearer token from
// localStorage, no AbortController/timeout wrapper. getForecastCount/getForecastHistory
// use this directly (not fetchWithTimeout) since it's a fast, simple read.
function forecastAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? {'Authorization': `Bearer ${token}`} : {};
}

/** Number of completed forecast jobs for a report. Returns 0 on any error. */
export const getForecastCount = async (reportId) => {
    try {
        const response = await fetch(
            `${getApiBase()}/analysis/forecast/jobs/count?reportId=${encodeURIComponent(reportId)}`,
            {headers: forecastAuthHeaders()},
        );
        if (!response.ok) return 0;
        const data = await response.json();
        return typeof data?.count === 'number' ? data.count : 0;
    } catch {
        return 0;
    }
};

/**
 * Forecast History View: every completed forecast job for a report, newest first. Each item
 * includes {@code requestContext} (horizon/creativity/personaHandles/userInstructions captured
 * at generation time) and the full {@code result}. Returns [] on any error.
 */
export const getForecastHistory = async (reportId) => {
    try {
        const response = await fetch(
            `${getApiBase()}/analysis/forecast/jobs/history?reportId=${encodeURIComponent(reportId)}`,
            {headers: forecastAuthHeaders()},
        );
        if (!response.ok) return [];
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch {
        return [];
    }
};
